import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import PUBLIC from "@/data/gatePublic.json";
import { GATES } from "@/data/temple";
import { slugify } from "@/lib/intro";
import { GrandSigil } from "./Sigil";
import { ease } from "@/lib/scroll";
import { chimeOn, playBell } from "@/lib/chime";
import { ChimeToggle } from "./ChimeToggle";
import { ReflectionForm } from "./Reflections";
import { REFLECTION_PROMPTS } from "@/data/reflectionPrompts";
import { useAuth } from "@/lib/auth";

const pad = (n) => String(n).padStart(2, "0");

export const getBlessing = (i) => {
  const b = PUBLIC.gates[i]?.blessing ?? {};
  return { body: b.body ?? "", call: b.call ?? "", signoff: (b.signoff ?? "With love,").replace(/\s*Goddess Ariel.*$/, "") };
};

export default function Blessing({ i, open, onClose }) {
  const next = GATES[i + 1];
  const b = getBlessing(i);
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return undefined;
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !chimeOn()) return undefined;
    const t = setTimeout(() => playBell(), 350);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="gate-blessing"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[90] flex items-start justify-center bg-[#0A0706]/90 backdrop-blur-xl px-5 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35 } }}
          transition={{ duration: 0.6 }}
          onClick={onClose}
          data-lenis-prevent
        >
          <div className="pointer-events-none fixed inset-0 flex items-center justify-center">
          <motion.div
            initial={{ scale: 0.6, opacity: 0, rotate: -40 }}
            animate={{ scale: 1, opacity: 0.25, rotate: 0 }}
            transition={{ duration: 2.2, ease }}
            className="shrink-0 w-[160vw] sm:w-[90vh] max-w-[1100px]"
          >
            <GrandSigil className="w-full h-full" />
          </motion.div>
          </div>
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0, transition: { duration: 0.3, delay: 0 } }}
            transition={{ duration: 1.1, delay: 0.3, ease }}
            className="relative max-w-2xl w-full text-center py-16 my-auto"
          >
            <button data-testid="blessing-close" onClick={onClose} aria-label="Close" className="absolute top-2 right-0 text-[#CFC2B6] hover:text-[#D9B47A]">
              <X className="w-6 h-6" />
            </button>
            <p className="font-tt-mono text-[12px] uppercase tracking-[0.4em] text-[#D9B47A]">Gate {pad(i + 1)} · Mastered</p>
            <h2 className="font-display text-[clamp(2.6rem,7vw,5rem)] leading-[1] mt-6">
              {GATES[i].name.split(" ").map((w, k) => (
                <span key={k} className={k % 2 ? "italic text-[#D9B47A]" : ""}>{w} </span>
              ))}
            </h2>
            <p data-testid="blessing-body" className="font-display text-xl sm:text-2xl leading-[1.5] text-[#E4D8CC] mt-8">{b.body}</p>
            {b.call && <p className="font-display italic text-2xl sm:text-3xl text-[#D9B47A] mt-6">{b.call}</p>}
            <p className="mt-8 text-[#CFC2B6]">{b.signoff}<br /><span className="font-display italic text-xl text-[#F2E8DF]">Goddess Ariel</span></p>
            {user?.initiate && <ReflectionForm gate={i} prompt={REFLECTION_PROMPTS[i]} className="mt-12" />}
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              {next ? (
                <Link
                  to={`/gates/${slugify(next.name)}`}
                  data-testid="blessing-next-gate"
                  onClick={onClose}
                  className="group inline-flex items-center gap-3 rounded-full bg-[#D9B47A] text-[#0A0706] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors"
                >
                  Enter Gate {pad(i + 2)} · {next.name}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
                </Link>
              ) : (
                <Link to="/journey" data-testid="blessing-next-gate" onClick={onClose} className="inline-flex items-center gap-3 rounded-full bg-[#C2414F] text-[#F2E8DF] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">
                  See your whole journey <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
              <button data-testid="blessing-stay" onClick={onClose} className="rounded-full border border-[#F2E8DF]/30 px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:border-[#D9B47A] hover:text-[#D9B47A] transition-colors">
                Stay with this gate
              </button>
            </div>
            <ChimeToggle className="mt-8" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
